import { environment } from 'environment';
import { B4KUser } from 'types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { devtools, getStorage } from './helpers';

interface UserState {
  user: B4KUser;
  setUser: (user: B4KUser) => void;
  updateUser(user: Partial<B4KUser>): void;
  removeUser: () => void;

  _rehydrated: boolean;
  _setRehydrated: (_rehydrated: boolean) => void;
  _fresh: boolean;
  _refresh: () => Promise<void>;

  _reset: () => void;
}

export const useUser = create<UserState>()(
  devtools(
    persist(
      set => ({
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        user: {} as B4KUser,
        setUser: (user: B4KUser) => set({ user }),
        updateUser: (user: Partial<B4KUser>) => set(state => ({ user: { ...state.user, ...user } })),
        removeUser: () => set({}),

        _rehydrated: false satisfies boolean,
        _setRehydrated: _rehydrated => set({ _rehydrated }),
        _fresh: false satisfies boolean,
        _refresh: async () => {
          const userRes = await import('networking').then(({ UserNet }) => UserNet.getMe());
          if (userRes.success === false) return;

          set(state => ({ ...state, user: userRes.data, _fresh: true }));
        },

        _reset: () => {
          useUser.persist.clearStorage();
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          set({ user: {} as B4KUser, _rehydrated: true, _fresh: false });
        },
      }),
      {
        name: `b4k-${environment.name}-user`,
        version: 1,
        onRehydrateStorage: () => state => state._setRehydrated(true),
        partialize: state => Object.fromEntries(Object.entries(state).filter(([key]) => !['_fresh'].includes(key))),
        storage: getStorage(),
      },
    ),
    { name: 'user' },
  ),
);
